/**
 * Document Service - Phases 1–3
 *
 * Phase 1: Case Studies (Hugo_CaseStudies)
 * Phase 2: Document Upload & Tracking (Hugo_Documents)
 * Phase 3: Risk Alerts (Hugo_RiskAlerts) & Guidance Content (Hugo_GuidanceContent)
 *
 * Firestore CRUD functions using modular Firebase SDK v9+ with tree-shaking.
 */
import { db, storage } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  onSnapshot,
  updateDoc,
  deleteDoc,
  Timestamp,
  arrayUnion,
  arrayRemove,
  documentId as fsDocumentId,
} from 'firebase/firestore';
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { collections } from '../utils/firestore';

/**
 * Fetch case studies with optional filters.
 *
 * @param {Object} [filters={}] - Query filters
 * @param {string} [filters.visibility] - Filter by visibility: 'global' | 'org_only' | 'ppi_only'
 * @param {string} [filters.status] - Filter by status: 'draft' | 'published' | 'archived'
 * @param {string} [filters.organizationId] - Filter by organization ID
 * @param {string} [filters.industry] - Filter by industry
 * @param {string} [filters.source] - Filter by source: 'csb' | 'internal' | 'audit' | 'observation' | 'regulatory' | 'other'
 * @param {number} [filters.limit] - Maximum number of results to return
 * @param {'asc'|'desc'} [filters.orderBy] - Sort direction for createdAt (default: 'desc')
 * @returns {Promise<Array<{id: string, ...data}>>} Array of case study objects
 */
export async function getCaseStudies(filters = {}) {
  const ref = collection(db, collections.caseStudies);
  const constraints = [];

  if (filters.visibility) {
    constraints.push(where('visibility', '==', filters.visibility));
  }

  if (filters.status) {
    constraints.push(where('status', '==', filters.status));
  }

  if (filters.organizationId) {
    constraints.push(where('organizationId', '==', filters.organizationId));
  }

  if (filters.industry) {
    constraints.push(where('industry', '==', filters.industry));
  }

  if (filters.source) {
    constraints.push(where('source', '==', filters.source));
  }

  // Default sort by createdAt descending
  const sortDirection = filters.orderBy || 'desc';
  constraints.push(orderBy('createdAt', sortDirection));

  if (filters.limit) {
    constraints.push(firestoreLimit(filters.limit));
  }

  const q = query(ref, ...constraints);
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}


const LIBRARY_PREF_TTL_MS = 60 * 1000;
const libraryPrefCache = new Map(); // orgId → { value, at }
/** Read `library.includeIndustryLibrary` for an organization (default true), cached for a minute. */
export async function orgIncludesIndustryLibrary(organizationId) {
  if (!organizationId) return true;
  const hit = libraryPrefCache.get(organizationId);
  if (hit && Date.now() - hit.at < LIBRARY_PREF_TTL_MS) return hit.value;
  let value = true;
  try {
    const snap = await getDoc(doc(db, 'Hugo_Organizations', organizationId));
    value = snap.exists() ? snap.data()?.library?.includeIndustryLibrary !== false : true;
  } catch { value = true; }
  libraryPrefCache.set(organizationId, { value, at: Date.now() });
  return value;
}
/** Forget the cached preference (called after the switch is saved). */
export function invalidateLibraryPrefCache(organizationId) { if (organizationId) libraryPrefCache.delete(organizationId); else libraryPrefCache.clear(); }

/**
 * Case studies the viewer may see (P7-11 / P5-15): the shared library (visibility 'global' — CSB
 * reports and documents organizations chose to share) plus the viewer's own organization's
 * studies. Two queries merged, newest first; legacy docs without `visibility` count as global.
 * Firestore rules enforce the same fence (an `org_only` study is readable only by its members),
 * so a single unconstrained query would be rejected.
 *
 * @param {string|null} organizationId - the viewer's active organization (null → shared only)
 * @param {Object} [filters] - same optional filters as getCaseStudies (status, industry, source, limit)
 */
export async function getVisibleCaseStudies(organizationId, filters = {}) {
  const { visibility, organizationId: _ignored, includeIndustryLibrary, ...rest } = filters;
  // Learning Library preference (Aug 25): an organization may hide the industry library.
  // Callers that already hold the organization pass `includeIndustryLibrary`; otherwise the
  // organization document is read once (cached briefly) so every list honours the switch.
  const includeShared = includeIndustryLibrary != null ? includeIndustryLibrary !== false : await orgIncludesIndustryLibrary(organizationId);
  const [shared, own] = await Promise.all([
    includeShared ? getCaseStudies({ ...rest, visibility: 'global' }) : Promise.resolve([]),
    organizationId ? getCaseStudies({ ...rest, organizationId }) : Promise.resolve([]),
  ]);
  const seen = new Set();
  const out = [];
  for (const cs of [...own, ...shared]) {
    if (seen.has(cs.id)) continue;
    seen.add(cs.id);
    out.push(cs);
  }
  const t = (v) => (v && typeof v.toMillis === 'function' ? v.toMillis() : v ? new Date(v).getTime() : 0);
  out.sort((a, b) => t(b.createdAt) - t(a.createdAt));
  return filters.limit ? out.slice(0, filters.limit) : out;
}

/**
 * Fetch a single case study by ID.
 *
 * @param {string} caseStudyId - The document ID of the case study
 * @returns {Promise<{id: string, ...data} | null>} The case study object, or null if not found
 */
export async function getCaseStudy(caseStudyId) {
  const ref = doc(db, collections.caseStudies, caseStudyId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

// ---------------------------------------------------------------------------
// Phase 2 — Document Upload & Tracking (Hugo_Documents)
// ---------------------------------------------------------------------------

/**
 * Upload a document to Firebase Storage and create a tracking record in Firestore.
 *
 * Creates a Hugo_Documents record with status 'uploading', uploads the file to
 * Firebase Storage at organizations/{orgId}/uploads/{timestamp}_{filename},
 * then updates the record with the download URL and status 'uploaded'.
 *
 * @param {string} orgId - Organization ID
 * @param {File} file - The File object to upload
 * @param {Object} [metadata={}] - Additional metadata for the document record
 * @param {string} [metadata.title] - Human-readable title (defaults to file name)
 * @param {string} [metadata.description] - Description of the document
 * @param {string} [metadata.category] - Document category (P5-13)
 * @param {string} [metadata.targetVisibility] - 'global' shares to the REPSit Common Database after review, 'org_only' (default) keeps it inside the org (P5-15)
 * @param {string} [metadata.uploadedBy] - UID of the uploader
 * @param {Function} [onProgress] - Optional callback receiving upload progress (0–100)
 * @returns {Promise<{docId: string, downloadURL: string}>} The Firestore document ID and download URL
 */
export async function uploadDocument(orgId, file, metadata = {}, onProgress) {
  if (!orgId) throw new Error('orgId is required');
  if (!file) throw new Error('file is required');

  // 1. Create the Firestore tracking record first (status: uploading)
  const now = Timestamp.now();
  const docData = {
    organizationId: orgId,
    fileName: file.name,
    fileSize: file.size,
    contentType: file.type,
    title: metadata.title || file.name,
    description: metadata.description || '',
    category: metadata.category || '',
    documentSource: metadata.documentSource === 'industry' ? 'industry' : 'organization', // Tim Rev 2 T32
    targetVisibility: metadata.targetVisibility === 'global' ? 'global' : 'org_only',
    uploadedBy: metadata.uploadedBy || '',
    status: 'uploading',
    uploadedAt: now,
    createdAt: now,
    storageUrl: null,
    downloadURL: null,
  };

  const colRef = collection(db, collections.documents);
  const docRef = await addDoc(colRef, docData);
  const docId = docRef.id;

  try {
    // 2. Upload file to Firebase Storage
    const storagePath = `organizations/${orgId}/uploads/${Date.now()}_${file.name}`;
    const storageRef = ref(storage, storagePath);
    const uploadTask = uploadBytesResumable(storageRef, file, {
      contentType: file.type,
      customMetadata: {
        organizationId: orgId,
        documentId: docId,
        uploadedBy: metadata.uploadedBy || '',
      },
    });

    // 3. Track upload progress
    const downloadURL = await new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          );
          if (typeof onProgress === 'function') {
            onProgress(progress);
          }
        },
        (error) => {
          // Update Firestore record to reflect failure
          updateDoc(docRef, {
            status: 'error',
            error: error.message || 'Upload failed',
            updatedAt: Timestamp.now(),
          }).catch(() => {}); // best-effort
          reject(error);
        },
        async () => {
          // Upload completed — get download URL
          try {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(url);
          } catch (err) {
            reject(err);
          }
        }
      );
    });

    // 4. Update Firestore record with download URL and status
    await updateDoc(docRef, {
      status: 'uploaded',
      storageUrl: storagePath,
      downloadURL,
      updatedAt: Timestamp.now(),
    });

    return { docId, downloadURL };
  } catch (error) {
    // Mark the document as errored if anything goes wrong after creation
    await updateDoc(docRef, {
      status: 'error',
      error: error.message || 'Upload failed',
      updatedAt: Timestamp.now(),
    }).catch(() => {}); // best-effort
    throw error;
  }
}

/**
 * Fetch documents for an organization, ordered by upload date (newest first).
 *
 * @param {string} orgId - Organization ID
 * @param {Object} [filters={}] - Optional filters
 * @param {string} [filters.status] - Filter by status: 'uploading' | 'uploaded' | 'processing' | 'completed' | 'error'
 * @param {string} [filters.category] - Filter by category
 * @param {number} [filters.limit] - Maximum number of results
 * @returns {Promise<Array<{id: string, ...data}>>} Array of document objects
 */
export async function getDocuments(orgId, filters = {}) {
  const colRef = collection(db, collections.documents);
  const constraints = [where('organizationId', '==', orgId)];

  if (filters.status) {
    constraints.push(where('status', '==', filters.status));
  }

  if (filters.category) {
    constraints.push(where('category', '==', filters.category));
  }

  constraints.push(orderBy('uploadedAt', 'desc'));

  if (filters.limit) {
    constraints.push(firestoreLimit(filters.limit));
  }

  const q = query(colRef, ...constraints);
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Subscribe to real-time updates on a single document's status.
 *
 * Useful for tracking upload progress and processing pipeline status changes
 * (e.g., uploading → uploaded → processing → completed).
 *
 * @param {string} docId - The Hugo_Documents document ID
 * @param {Function} callback - Called with {id, ...data} or null on each update
 * @returns {Function} Unsubscribe function — call to stop listening
 */
export function subscribeToDocument(docId, callback) {
  const docRef = doc(db, collections.documents, docId);
  return onSnapshot(docRef, (snap) => {
    if (!snap.exists()) {
      callback(null);
    } else {
      callback({ id: snap.id, ...snap.data() });
    }
  });
}

// ---------------------------------------------------------------------------
// Phase 3 — Risk Alerts (Hugo_RiskAlerts)
// ---------------------------------------------------------------------------

/**
 * Fetch active risk alerts for an organization, ordered by newest first.
 *
 * @param {string} orgId - Organization ID
 * @returns {Promise<Array<{id: string, ...data}>>} Array of active risk alert objects
 */
export async function getRiskAlerts(orgId) {
  const ref = collection(db, collections.riskAlerts);
  const q = query(
    ref,
    where('organizationId', '==', orgId),
    where('status', '==', 'active'),
    orderBy('createdAt', 'desc'),
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Subscribe to real-time updates on active risk alerts for an organization.
 *
 * @param {string} orgId - Organization ID
 * @param {Function} callback - Called with array of {id, ...data} on each update
 * @returns {Function} Unsubscribe function
 */
export function subscribeToRiskAlerts(orgId, callback) {
  const ref = collection(db, collections.riskAlerts);
  const q = query(
    ref,
    where('organizationId', '==', orgId),
    where('status', '==', 'active'),
    orderBy('createdAt', 'desc'),
  );
  return onSnapshot(q, (snapshot) => {
    const alerts = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    callback(alerts);
  });
}

/**
 * Acknowledge a risk alert for the CURRENT USER only.
 *
 * Per-user acknowledgement: the user's UID is appended to `acknowledgedBy`
 * (array) and the alert is hidden from THEIR view by useRiskAlerts. The alert
 * itself stays `status: 'active'` for everyone else in the organization —
 * and keeps counting toward the HP Barometer's risk-alert component.
 *
 * (Previously this flipped `status` to 'acknowledged', which hid the alert
 * org-wide and permanently — one user's click destroyed the alert for every
 * user in the org, with no undo in the UI. The org-wide `status` field is
 * now reserved for real lifecycle transitions: resolution by an authorized
 * workflow/admin, or expiry.)
 *
 * @param {string} alertId - The document ID of the risk alert
 * @param {string} uid - The UID of the user acknowledging the alert
 * @returns {Promise<void>}
 */
export async function acknowledgeAlert(alertId, uid) {
  if (!uid) throw new Error('acknowledgeAlert requires the current user\'s uid');
  const ref = doc(db, collections.riskAlerts, alertId);
  await updateDoc(ref, {
    acknowledgedBy: arrayUnion(uid),
    lastAcknowledgedAt: Timestamp.now(),
  });
}

/**
 * Un-acknowledge every active alert of an organization for ONE user (P3-06 "Restore demo alerts"):
 * a tester who dismissed Magnum's alerts gets them back without touching anyone else's view.
 * @returns {number} alerts restored
 */
export async function restoreAlertsForUser(organizationId, uid) {
  if (!organizationId || !uid) return 0;
  const q = query(
    collection(db, collections.riskAlerts),
    where('organizationId', '==', organizationId),
    where('acknowledgedBy', 'array-contains', uid)
  );
  const snap = await getDocs(q);
  await Promise.all(snap.docs.map((d) => updateDoc(d.ref, { acknowledgedBy: arrayRemove(uid) })));
  return snap.size;
}

// ---------------------------------------------------------------------------
// Phase 3 — Guidance Content (Hugo_GuidanceContent)
// ---------------------------------------------------------------------------

/**
 * Fetch guidance content matching an assessment type, then filter client-side
 * by which REPS scores fall within the guidance's score ranges.
 *
 * Score range matching logic:
 *   Each guidance doc may have a `scoreRanges` map keyed by quadrant
 *   (e.g. { reliability: { min: 0, max: 4 }, efficiency: { min: 0, max: 5 } }).
 *   A guidance doc matches if ANY quadrant score falls within its range.
 *
 * @param {string} assessmentType - Assessment type identifier (e.g. 'drift', 'accumulation')
 * @param {{ reliability?: number, efficiency?: number, productivity?: number, safety?: number }} scores
 *   Current REPS quadrant scores
 * @returns {Promise<Array<{id: string, ...data}>>} Matching guidance content
 */
export async function getGuidanceForScores(assessmentType, scores) {
  const ref = collection(db, collections.guidanceContent);
  const q = query(
    ref,
    where('assessmentType', '==', assessmentType),
    where('status', '==', 'published'),
    orderBy('principleOrElement', 'asc'),
  );
  const snapshot = await getDocs(q);
  const allGuidance = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

  // Client-side score-range matching
  return allGuidance.filter((guidance) => {
    const ranges = guidance.scoreRanges;
    if (!ranges || typeof ranges !== 'object') return true; // No ranges → always include

    // Match if ANY quadrant score falls within the guidance's defined range
    return Object.entries(ranges).some(([quadrant, range]) => {
      const score = scores[quadrant];
      if (score == null || range == null) return false;
      const min = typeof range.min === 'number' ? range.min : -Infinity;
      const max = typeof range.max === 'number' ? range.max : Infinity;
      return score >= min && score <= max;
    });
  });
}

/**
 * Cancel an in-progress case study analysis.
 * Sets status to 'cancelled' so the frontend can hide it and so any subsequent
 * n8n workflow PATCHes can early-out by checking status before updating.
 * The n8n workflow itself isn't killed — it finishes in the background — but
 * the user-visible state is updated immediately.
 *
 * @param {string} caseStudyId - Either cs_{documentId} or the full Firestore doc ID
 * @param {string} cancelledBy - UID of the user who cancelled
 */
export async function cancelCaseStudy(caseStudyId, cancelledBy) {
  if (!caseStudyId) throw new Error('caseStudyId is required');
  const ref = doc(db, collections.caseStudies, caseStudyId);
  await updateDoc(ref, {
    status: 'cancelled',
    cancelledBy: cancelledBy || null,
    cancelledAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });
}

/**
 * Delete a case study + the underlying document + all derived records.
 * This is the user-friendly entry point — UI calls this when the user clicks
 * "Delete" on a case study. Internally it calls deleteDocument(sourceDocumentId)
 * which already cascades to all derived collections.
 *
 * @param {Object} caseStudy - The case study object (must have sourceDocumentId or id like cs_X)
 * @returns {Promise<{deleted: string[], errors: Array}>}
 */
export async function deleteCaseStudyWithCascade(caseStudy) {
  if (!caseStudy) throw new Error('caseStudy is required');
  // Derive the source document ID — either explicit field or strip 'cs_' prefix
  const sourceDocId = caseStudy.sourceDocumentId
    || (typeof caseStudy.id === 'string' && caseStudy.id.startsWith('cs_')
      ? caseStudy.id.slice(3)
      : null);
  if (!sourceDocId) {
    // No underlying document — just delete the case study record itself
    await deleteDoc(doc(db, collections.caseStudies, caseStudy.id));
    return { deleted: [`${collections.caseStudies}/${caseStudy.id}`], errors: [] };
  }
  return deleteDocument(sourceDocId);
}

/**
 * Delete a document and all its analysis-derived records.
 *
 * Cascades:
 *   - Storage file at doc.storageUrl
 *   - Hugo_Documents/{id}
 *   - Hugo_CaseStudies/cs_{id}        (if exists)
 *   - Hugo_DocumentText/{id}          (if exists — n8n ingest cache)
 *   - Hugo_RiskAlerts/alert_{id}      (if exists)
 *   - Hugo_PatternLibrary/pat_{id}    (if exists)
 *   - Hugo_GuidanceContent/guide_{id} (if exists)
 *   - Hugo_OEAdjustments/adj_{id}     (if exists)
 *
 * @param {string} documentId - The Hugo_Documents document ID
 * @returns {Promise<{deleted: string[], errors: Array<{path: string, error: string}>}>}
 */
export async function deleteDocument(documentId) {
  if (!documentId) {
    throw new Error('deleteDocument: documentId is required');
  }

  const deleted = [];
  const errors = [];

  // ---- 1) Read the source doc to get the storage path ----
  const sourceRef = doc(db, collections.documents, documentId);
  let sourceData = null;
  try {
    const snap = await getDoc(sourceRef);
    if (snap.exists()) {
      sourceData = snap.data();
    }
  } catch (err) {
    errors.push({ path: `${collections.documents}/${documentId}`, error: err.message });
  }

  // ---- 2) Delete the storage file (best-effort) ----
  if (sourceData?.storageUrl) {
    try {
      const fileRef = ref(storage, sourceData.storageUrl);
      await deleteObject(fileRef);
      deleted.push(`storage:${sourceData.storageUrl}`);
    } catch (err) {
      // Don't block on storage delete failure — file may already be gone
      if (err.code !== 'storage/object-not-found') {
        errors.push({ path: `storage:${sourceData.storageUrl}`, error: err.message });
      }
    }
  }

  // ---- 3) Cascade delete derived analysis records ----
  const cascades = [
    { collection: collections.caseStudies, id: `cs_${documentId}` },
    { collection: collections.documentText, id: documentId },        // n8n text cache
    { collection: collections.riskAlerts, id: `alert_${documentId}` },
    { collection: collections.patternLibrary, id: `pat_${documentId}` },
    { collection: collections.guidanceContent, id: `guide_${documentId}` },
    { collection: collections.oeAdjustments, id: `adj_${documentId}` },
  ];

  for (const { collection: col, id } of cascades) {
    if (!col) continue; // Skip if collection name not configured
    try {
      const ref = doc(db, col, id);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        await deleteDoc(ref);
        deleted.push(`${col}/${id}`);
      }
    } catch (err) {
      errors.push({ path: `${col}/${id}`, error: err.message });
    }
  }

  // ---- 4) Delete the Hugo_Documents record itself ----
  try {
    await deleteDoc(sourceRef);
    deleted.push(`${collections.documents}/${documentId}`);
  } catch (err) {
    errors.push({ path: `${collections.documents}/${documentId}`, error: err.message });
  }

  return { deleted, errors };
}

/**
 * Load the extracted text cache for a set of documents (P5-08 full-text search).
 * `Hugo_DocumentText/{documentId}` is written by the ingest workflow; keyed here by documentId.
 * Fetched in `in` batches of 30 (Firestore limit); missing docs are simply absent from the map.
 *
 * @param {string[]} documentIds
 * @returns {Promise<Object<string, string>>} documentId → fullText
 */
export async function getDocumentTexts(documentIds = []) {
  const ids = [...new Set((documentIds || []).filter(Boolean))];
  const out = {};
  for (let i = 0; i < ids.length; i += 30) {
    const chunk = ids.slice(i, i + 30);
    const snap = await getDocs(query(collection(db, collections.documentText), where(fsDocumentId(), 'in', chunk)));
    snap.forEach((d) => {
      const data = d.data() || {};
      out[d.id] = data.fullText || (Array.isArray(data.chunks) ? data.chunks.join('\n') : '');
    });
  }
  return out;
}
